import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axiosInstance from "../utils/api";
import {
    FiCheckCircle, FiXCircle, FiArrowLeft, FiMail, FiPhone,
    FiCreditCard, FiUser, FiMapPin, FiCheckSquare, FiX,
    FiAlertCircle, FiUserPlus, FiAlertTriangle, FiSend, FiCheck,
    FiBriefcase,
} from "react-icons/fi";
import toast from "react-hot-toast";
import QRCode from "react-qr-code";
import { FaWhatsapp } from "react-icons/fa";
import { FaStar } from "react-icons/fa6";
import { useBulkSend } from "../utils/Bulksendcontext";

const DEFAULT_HEADER = {
    image: null,
    imagePath: null,
    height: 90,
    overlay: false,
    overlayColor: "#000000",
    overlayOpacity: 40,
    custom: {
        bgColor: "#0f172a",
        bgColor2: "#1e3a5f",
        gradientDir: "to right",
        showLogo: true,
        logoSize: 44,
        showEventName: true,
        eventNameFontSize: 15,
        eventNameColor: "#ffffff",
        eventNameWeight: "bold",
        showTagline: false,
        taglineText: "Badge d'Accès Officiel",
        taglineFontSize: 9,
        taglineColor: "#94a3b8",
        layout: "logo-left",
        padding: 16,
    },
};

const DEFAULT_LAYOUT = {
    logo: { visible: true, position: "top", size: 48, align: "left" },
    eventName: { visible: true, fontSize: 14, fontWeight: "extrabold", color: "#0f172a", align: "left" },
    tagline: { visible: true, text: "Badge d'Accès Officiel", fontSize: 9, color: "#64748b", opacity: 85 },
    name: { visible: true, fontSize: 14, fontWeight: "bold", color: "#ffffff" },
    email: { visible: true, fontSize: 10, color: "#ffffff" },
    cin: { visible: true, fontSize: 11, color: "#ffffff", prefix: "CIN. NO: " },
    qrCode: { visible: true, size: 90, border: true, borderColor: "#cbd5e1" },
    footer: { visible: true, text: "ACCESS ALL AREAS • 24 HOURS" },
};

const DEFAULT_TYPE_CONFIG = {
    builder: { label: "BUILDER", subLabel: "OFFICIAL BUILDER", bodyColor: "#111827", accentColor: "#1f2937", layout: null },
    full_access: { label: "STAFF", subLabel: "OFFICIAL STAFF", bodyColor: "#14532d", accentColor: "#166534", layout: null },
    contractor: { label: "CONTRACTOR", subLabel: "OFFICIAL CONTRACTOR", bodyColor: "#991b1b", accentColor: "#7f1d1d", layout: null },
};

const resolveDesignKey = (type) => {
    if (type === "build_up" || type === "show_days" || type === "teardown") return "builder";
    if (type === "full_access") return "full_access";
    if (type === "contractor") return "contractor";
    return "builder";
};

const merge = (target, source) => {
    if (!source) return target;
    const out = { ...target };
    for (const key of Object.keys(source)) {
        if (source[key] && typeof source[key] === "object" && !Array.isArray(source[key])) {
            out[key] = merge(target[key] || {}, source[key]);
        } else if (source[key] !== undefined && source[key] !== null) {
            out[key] = source[key];
        }
    }
    return out;
};

const CustomHeaderView = ({ cfg, logoUrl, eventName, height }) => {
    const c = merge(DEFAULT_HEADER.custom, cfg || {});
    const bg = `linear-gradient(${c.gradientDir}, ${c.bgColor}, ${c.bgColor2})`;
    const pad = c.padding || 16;

    const LogoEl = logoUrl && c.showLogo
        ? <img src={logoUrl} alt="logo" style={{ height: c.logoSize || 44, objectFit: "contain", flexShrink: 0 }} />
        : null;

    const NameEl = c.showEventName
        ? (
            <div style={{ display: "flex", flexDirection: "column", justifyContent: "center", overflow: "hidden" }}>
                <div style={{
                    fontSize: c.eventNameFontSize || 15,
                    fontWeight: c.eventNameWeight === "bold" ? 700 : c.eventNameWeight === "extrabold" ? 800 : 600,
                    color: c.eventNameColor || "#ffffff",
                    lineHeight: 1.2,
                    whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                }}>
                    {eventName || "Event Name"}
                </div>
                {c.showTagline && (
                    <div style={{ fontSize: c.taglineFontSize || 9, color: c.taglineColor || "#94a3b8", marginTop: 3, letterSpacing: 0.5 }}>
                        {c.taglineText || "Badge d'Accès Officiel"}
                    </div>
                )}
            </div>
        )
        : null;

    const renderLayout = () => {
        switch (c.layout) {
            case "logo-left":
                return (
                    <div style={{ display: "flex", alignItems: "center", gap: 12, padding: `0 ${pad}px`, width: "100%", height: "100%" }}>
                        {LogoEl}{NameEl}
                    </div>
                );
            case "logo-right":
                return (
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, padding: `0 ${pad}px`, width: "100%", height: "100%" }}>
                        {NameEl}{LogoEl}
                    </div>
                );
            case "logo-top":
                return (
                    <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 6, padding: `${pad / 2}px ${pad}px`, width: "100%", height: "100%" }}>
                        {LogoEl}
                        {NameEl && <div style={{ textAlign: "center" }}>{NameEl}</div>}
                    </div>
                );
            case "logo-only":
                return (
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", width: "100%", height: "100%" }}>
                        {LogoEl}
                    </div>
                );
            case "text-only":
                return (
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: `0 ${pad}px`, width: "100%", height: "100%" }}>
                        {NameEl && <div style={{ textAlign: "center" }}>{NameEl}</div>}
                    </div>
                );
            default:
                return null;
        }
    };

    return (
        <div style={{
            width: "100%", height: height || 90,
            background: bg,
            flexShrink: 0,
            display: "flex", alignItems: "center",
            overflow: "hidden",
        }}>
            {renderLayout()}
        </div>
    );
};

export const BadgeCard = ({ badge, standiste, eventName, badgeConfig }) => {
    const designKey = resolveDesignKey(badge.type);
    const savedTypes = badgeConfig?.types || {};

    const savedTypeConfig = savedTypes[designKey] || savedTypes[badge.type] || {};
    const defaultConfig = DEFAULT_TYPE_CONFIG[designKey];
    const typeConfig = { ...defaultConfig, ...savedTypeConfig };

    const globalLayout = badgeConfig?.globalLayout
        ? merge(DEFAULT_LAYOUT, badgeConfig.globalLayout)
        : DEFAULT_LAYOUT;
    const layout = typeConfig.layout ? merge(globalLayout, typeConfig.layout) : globalLayout;

    const bodyColor = typeConfig.bodyColor || defaultConfig.bodyColor;
    const accentColor = typeConfig.accentColor || defaultConfig.accentColor;
    const label = typeConfig.label || defaultConfig.label;
    const subLabel = typeConfig.subLabel || defaultConfig.subLabel;
    const logoUrl = badgeConfig?.logo;
    const font = globalLayout.font || "ui-sans-serif, system-ui, sans-serif";

    // ── Header config ──────────────────────────────────────────────────────
    const hdr = merge(DEFAULT_HEADER, badgeConfig?.header || {});
    const hasImage = !!hdr.image;
    const headerHeight = hdr.height || 90;

    const renderHeader = () => {
        if (hasImage) {
            return (
                <div style={{ flexShrink: 0, lineHeight: 0, height: headerHeight, position: "relative", overflow: "hidden" }}>
                    <img
                        src={hdr.image}
                        alt="header"
                        style={{ width: "100%", height: "100%", display: "block", objectFit: "cover" }}
                    />
                    {hdr.overlay && (
                        <div style={{
                            position: "absolute", inset: 0,
                            backgroundColor: hdr.overlayColor || "#000000",
                            opacity: (hdr.overlayOpacity ?? 40) / 100,
                            pointerEvents: "none",
                        }} />
                    )}
                </div>
            );
        }

        // No image → use custom header builder
        return (
            <CustomHeaderView
                cfg={hdr.custom}
                logoUrl={logoUrl}
                eventName={eventName}
                height={headerHeight}
            />
        );
    };

    return (
        <div style={{
            width: "100%", height: "100%", background: "#fff",
            display: "flex", flexDirection: "column", fontFamily: font,
        }}>
            {renderHeader()}

            {/* Colored body */}
            <div style={{
                flex: 1, background: bodyColor,
                display: "flex", flexDirection: "column",
                alignItems: "center", justifyContent: "space-between",
                padding: "20px 18px 16px", position: "relative", overflow: "hidden",
            }}>
                {/* Diagonal texture */}
                <div style={{
                    position: "absolute", inset: 0, opacity: 0.04, pointerEvents: "none",
                    backgroundImage: "repeating-linear-gradient(45deg,#fff 0,#fff 1px,transparent 0,transparent 50%)",
                    backgroundSize: "20px 20px",
                }} />

                {/* Sub-label + main label */}
                <div style={{ textAlign: "center", zIndex: 1 }}>
                    <div style={{ fontSize: 9, color: "#fff", opacity: 0.55, letterSpacing: 3, textTransform: "uppercase", marginBottom: 6 }}>
                        {subLabel}
                    </div>
                    <div style={{
                        fontSize: label.length > 9 ? 26 : 34,
                        fontWeight: 900, color: "#fff",
                        letterSpacing: 0.5, lineHeight: 1.05,
                        textTransform: "uppercase", textAlign: "center",
                    }}>
                        {label}
                    </div>
                </div>

                {/* CIN + QR */}
                <div style={{ textAlign: "center", zIndex: 1, width: "100%" }}>
                    {layout.cin?.visible && standiste.cin && (
                        <div style={{
                            fontSize: layout.cin?.fontSize || 11,
                            color: layout.cin?.color || "#ffffff",
                            opacity: 0.8, marginBottom: 8, letterSpacing: 1, fontWeight: 600,
                        }}>
                            {layout.cin?.prefix || "CIN. NO: "}
                            <span style={{ fontFamily: "monospace" }}>{standiste.cin}</span>
                        </div>
                    )}
                    {layout.qrCode?.visible && (
                        <div style={{ display: "flex", justifyContent: "center" }}>
                            <div style={{
                                background: "#fff", padding: 8,
                                border: `3px solid ${accentColor}`,
                                borderRadius: 8, boxShadow: "0 4px 16px rgba(0,0,0,0.4)",
                            }}>
                                <QRCode value={badge._id} size={layout.qrCode?.size || 90} />
                            </div>
                        </div>
                    )}
                </div>

                {/* Name + email */}
                <div style={{ textAlign: "center", zIndex: 1 }}>
                    {layout.name?.visible && (
                        <div style={{
                            fontSize: layout.name?.fontSize || 14,
                            fontWeight: layout.name?.fontWeight === "bold" ? 700 : 600,
                            color: layout.name?.color || "#ffffff",
                            marginBottom: 2,
                        }}>
                            {standiste.name}
                        </div>
                    )}
                    {layout.email?.visible && standiste.email && (
                        <div style={{ fontSize: layout.email?.fontSize || 10, color: layout.email?.color || "#ffffff", opacity: 0.65 }}>
                            {standiste.email}
                        </div>
                    )}
                </div>

                {/* Footer */}
                {layout.footer?.visible && (
                    <div style={{ zIndex: 1, width: "100%", borderTop: "1px solid rgba(255,255,255,0.15)", paddingTop: 10, textAlign: "center" }}>
                        <div style={{ fontSize: 11, fontWeight: 800, color: "#fff", letterSpacing: 1.5, textTransform: "uppercase" }}>
                            {layout.footer?.text || "ACCESS ALL AREAS"}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export const TypePill = ({ type }) => {
    const designKey = resolveDesignKey(type);
    const styles = { builder: "bg-slate-800 text-white", contractor: "bg-red-700 text-white" };
    const labels = { builder: "Builder", contractor: "Contractor" };
    return (
        <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${styles[designKey]}`}>
            {labels[designKey]}
        </span>
    );
};

const StandisteStaff = () => {
    const { id, standisteId } = useParams();
    const navigate = useNavigate();
    const { startBulkSend, session } = useBulkSend();

    const [standiste, setStandiste] = useState(null);
    const [staff, setStaff] = useState([]);
    const [stands, setStands] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState("all");
    const [photoPreview, setPhotoPreview] = useState(null);
    const [error, setError] = useState(null);
    const [actionLoading, setActionLoading] = useState(null);
    const [confirmOpen, setConfirmOpen] = useState(false);
    const [pendingCount, setPendingCount] = useState(0);
    const [printBadge, setPrintBadge] = useState(null);
    const [rateLimitInfo, setRateLimitInfo] = useState(null);
    const [sendingQueue, setSendingQueue] = useState(new Set());
    const [standisteBadge, setStandisteBadge] = useState(null);
    const [badgeConfig, setBadgeConfig] = useState(null);

    const [selectionMode, setSelectionMode] = useState(false);
    const [selectedIds, setSelectedIds] = useState(new Set());

    const bulkRunning = session && !session.done;

    const isContractor = standiste?.role === "contractor";
    const roleLabel = isContractor ? "Contractor" : "Stand Builder";
    const accentColor = isContractor
        ? { ring: "ring-red-800", border: "border-red-800", bg: "bg-red-800", text: "text-red-700", pill: "bg-red-50 text-red-700" }
        : { ring: "ring-indigo-300", border: "border-indigo-400", bg: "bg-indigo-600", text: "text-indigo-600", pill: "bg-indigo-50 text-indigo-700" };

    useEffect(() => { fetchStandiste(); }, [standisteId]);

    const fetchStandiste = async () => {
        try {
            setLoading(true);
            setError(null);
            const res = await axiosInstance.get(`/auth/user/${standisteId}`);
            const standisteData = res.data.user;
            setStandiste(standisteData);
            setStands(standisteData.stands || []);
            try {
                const evRes = await axiosInstance.get(`/event/${id}`);
                setBadgeConfig(evRes.data.event?.badgeConfig || null);
            } catch { }
            await fetchData(standisteData);
        } catch (err) {
            if (err.response?.status === 404 || err.response?.data?.message?.includes("not found")) {
                setError("no_badge");
            } else {
                setError(err.response?.data?.message || "Failed to load data");
            }
            setLoading(false);
        }
    };

    const fetchData = async (standisteData) => {
        try {
            const res = await axiosInstance.get(`/badge/by-chef/${standisteData._id}`);
            const allBadges = res.data.badges || [];
            setStandisteBadge(allBadges.find((b) => b.standiste?._id === standisteData._id) || null);
            setStaff(allBadges.filter((b) => b.chef?._id === standisteData._id));
        } catch {
            setError("Failed to load staff data");
        } finally {
            setLoading(false);
        }
    };

    const updateStatus = async (badgeId, status) => {
        try {
            setActionLoading(badgeId);
            await axiosInstance.put(`/badge/${badgeId}`, { status });
            await fetchStandiste();
            toast.success(`Badge ${status === "active" ? "activated" : "deactivated"} successfully`);
        } catch {
            toast.error("Failed to update status");
        } finally {
            setActionLoading(null);
        }
    };

    const acceptAll = () => {
        const inactive = staff.filter((b) => b.status !== "active");
        if (inactive.length === 0) { toast.error("All staff members are already active"); return; }
        setPendingCount(inactive.length);
        setConfirmOpen(true);
    };

    const confirmAcceptAll = async () => {
        try {
            setActionLoading("accept-all");
            const inactive = staff.filter((b) => b.status !== "active");
            await Promise.all(inactive.map((b) => axiosInstance.put(`/badge/${b._id}`, { status: "active" })));
            toast.success(`${inactive.length} staff member(s) activated successfully`);
            setConfirmOpen(false);
            await fetchStandiste();
        } catch {
            toast.error("Failed to activate staff");
        } finally {
            setActionLoading(null);
        }
    };

    const handlePrintBadge = (badge, person) => {
        setPrintBadge({ badge, person });
        setTimeout(() => window.print(), 150);
    };

    const sendWhatsappBadge = async (badge, person) => {
        if (sendingQueue.has(badge._id)) { toast.error("Already sending this badge"); return; }
        setSendingQueue((prev) => new Set([...prev, badge._id]));
        const t = toast.loading("Generating PDF and sending via WhatsApp...");
        try {
            const res = await axiosInstance.post("/badge/send-whatsapp", {
                phone: person.phone, badgeId: badge._id,
                name: person.name, eventName: standiste?.event?.name || "Event",
                eventId: id,
                badgeConfig: badgeConfig || null,
            });
            const ws = res.data?.whatsappSentAt;
            if (ws) {
                setStandisteBadge((prev) => (prev && String(prev._id) === String(badge._id) ? { ...prev, whatsappSentAt: ws } : prev));
                setStaff((prev) => prev.map((b) => (String(b._id) === String(badge._id) ? { ...b, whatsappSentAt: ws } : b)));
            }
            toast.dismiss(t);
            toast.success("Badge PDF sent successfully via WhatsApp");
            setRateLimitInfo(null);
        } catch (error) {
            toast.dismiss(t);
            if (error.response?.status === 422) {
                toast.error(`Invalid phone number: ${person.phone}`, { duration: 6000 });
            } else if (error.response?.status === 429) {
                const retryAfter = error.response.data?.retry_after || 60;
                setRateLimitInfo({ retryAfter, message: error.response.data?.message || "Rate limit exceeded" });
                toast.error(`Rate limit reached. Wait ${retryAfter}s before sending.`, { duration: 5000 });
            } else {
                toast.error(error.response?.data?.message || "Failed to send badge PDF");
            }
        } finally {
            setSendingQueue((prev) => { const s = new Set(prev); s.delete(badge._id); return s; });
        }
    };

    const sendAccessEmail = async () => {
        try {
            setActionLoading("send-email");

            await axiosInstance.post("/auth/send-contractor-email", {
                id: standiste._id,
            });

            toast.success(
                standiste.isMailSent
                    ? "Access email resent successfully"
                    : "Access email sent successfully"
            );

            await fetchStandiste();
        } catch (error) {
            toast.error(
                error.response?.data?.message ||
                "Failed to send access email"
            );
        } finally {
            setActionLoading(null);
        }
    };

    const filteredStaff = staff.filter((b) => filter === "all" || b.status === filter);

    const allSelectableItems = useCallback(() => {
        const list = [];
        if (standisteBadge) {
            list.push({ badge: standisteBadge, person: { name: standiste?.name, email: standiste?.email, cin: standiste?.cin, phone: standiste?.phone } });
        }
        filteredStaff.forEach((badge) => {
            list.push({ badge, person: { name: badge.name, cin: badge.cin, phone: badge.phone, email: badge.email } });
        });
        return list.filter((i) => !!i.person.phone);
    }, [standisteBadge, filteredStaff, standiste]);

    const toggleSelect = (badgeId) => setSelectedIds((prev) => { const n = new Set(prev); n.has(badgeId) ? n.delete(badgeId) : n.add(badgeId); return n; });
    const selectAll = () => setSelectedIds(new Set(allSelectableItems().map((i) => i.badge._id)));
    const deselectAll = () => setSelectedIds(new Set());
    const exitSelectionMode = () => { setSelectionMode(false); setSelectedIds(new Set()); };

    const handleStartBulkSend = () => {
        const all = allSelectableItems();
        const chosen = all.filter((i) => selectedIds.has(i.badge._id));
        if (chosen.length === 0) { toast.error("No badges with phone numbers selected"); return; }
        startBulkSend({
            badges: chosen,
            eventName: standiste?.event?.name || "Event",
            eventId: id,
            onSendSuccess: ({ badgeId, whatsappSentAt }) => {
                setStandisteBadge((prev) => (prev && String(prev._id) === String(badgeId) ? { ...prev, whatsappSentAt } : prev));
                setStaff((prev) => prev.map((b) => (String(b._id) === String(badgeId) ? { ...b, whatsappSentAt } : b)));
            },
        });
        exitSelectionMode();
        toast.success(`Queue started: ${chosen.length} badge${chosen.length > 1 ? "s" : ""} will be sent`);
    };

    useEffect(() => {
        const afterPrint = () => setPrintBadge(null);
        window.addEventListener("afterprint", afterPrint);
        return () => window.removeEventListener("afterprint", afterPrint);
    }, []);

    const allBadges = [...(standisteBadge ? [standisteBadge] : []), ...staff];
    const activeCount = allBadges.filter((b) => b.status === "active").length;
    const inactiveCount = allBadges.filter((b) => b.status !== "active").length;
    const totalCount = allBadges.length;

    if (loading) {
        return (
            <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-50 flex items-center justify-center p-4">
                <div className="text-center">
                    <div className="animate-spin rounded-full h-16 w-16 border-4 border-slate-200 border-t-indigo-600 mx-auto" />
                    <p className="mt-6 text-slate-600 font-medium">Loading staff information...</p>
                </div>
            </div>
        );
    }

    if (error) {
        if (error === "no_badge") {
            return (
                <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-50 flex items-center justify-center p-4">
                    <div className="text-center max-w-md">
                        <div className="w-20 h-20 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-6">
                            <FiUserPlus className="text-indigo-600" size={40} />
                        </div>
                        <h3 className="text-2xl font-bold text-slate-900 mb-3">No Badge Found</h3>
                        <p className="text-slate-600 mb-6 leading-relaxed">Please create a badge for this {roleLabel.toLowerCase()} first.</p>
                        <button onClick={() => navigate(`/event/${id}/badging`)} className="inline-flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white px-6 py-3 rounded-lg font-medium transition-colors">
                            <FiArrowLeft size={18} /> Go Back to Badging
                        </button>
                    </div>
                </div>
            );
        }
        return (
            <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-50 flex items-center justify-center p-4">
                <div className="text-center">
                    <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <FiAlertCircle className="text-red-600" size={32} />
                    </div>
                    <h3 className="text-xl font-semibold text-slate-900 mb-2">Error Loading Data</h3>
                    <p className="text-slate-600 mb-6">{error}</p>
                    <button onClick={() => navigate(`/event/${id}/badging`)} className="inline-flex items-center gap-2 bg-indigo-600 hover:bg-indigo-700 text-white px-6 py-3 rounded-lg font-medium transition-colors">
                        <FiArrowLeft size={18} /> Go Back
                    </button>
                </div>
            </div>
        );
    }

    const StaffCard = ({ badge, person, isManager = false }) => {
        const isActive = badge.status === "active";
        const isSelected = selectedIds.has(badge._id);
        const hasPhone = !!person.phone;
        const badgeSentViaWhatsapp = !!badge.whatsappSentAt;
        const designKey = resolveDesignKey(badge.type);

        const dotColors = {
            builder: "bg-slate-700",
            contractor: "bg-red-700",
        };

        return (
            <div
                className={`bg-white border rounded-xl shadow-sm hover:shadow-md transition-all overflow-hidden relative
                    ${selectionMode && hasPhone ? "cursor-pointer" : ""}
                    ${isSelected ? `${accentColor.border} ring-2 ${accentColor.ring} shadow-sm` : "border-slate-200"}
                    ${selectionMode && !hasPhone ? "opacity-50" : ""}
                `}
                onClick={selectionMode && hasPhone ? () => toggleSelect(badge._id) : undefined}
            >
                {selectionMode && (
                    <div className={`absolute top-3 right-3 z-10 w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all
                        ${isSelected ? `${accentColor.bg} ${accentColor.border}` : hasPhone ? "bg-white border-slate-300" : "bg-slate-100 border-slate-200"}`}>
                        {isSelected && <FiCheck size={13} className="text-white" />}
                        {!hasPhone && !isSelected && <FiX size={11} className="text-slate-300" />}
                    </div>
                )}

                <div className="px-4 py-3 bg-slate-50 border-b border-slate-200">
                    <div className="flex items-start justify-between">
                        <div className="flex items-center gap-3">
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white flex-shrink-0 ${isManager ? accentColor.bg : dotColors[designKey] || "bg-slate-600"}`}>
                                {isManager
                                    ? (isContractor ? <FiBriefcase size={16} /> : <FaStar size={16} />)
                                    : <span className="font-bold text-sm">{person.name?.charAt(0)?.toUpperCase() || "U"}</span>
                                }
                            </div>
                            <div className="min-w-0">
                                <div className="flex items-center gap-2 flex-wrap">
                                    <h4 className="font-semibold text-slate-800 text-sm pr-8 truncate">{person.name}</h4>
                                </div>
                                <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                                    {isManager
                                        ? <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full ${isContractor ? "bg-red-700 text-white" : "bg-indigo-600 text-white"}`}>{roleLabel}</span>
                                        : <TypePill type={badge.type} />
                                    }
                                    {badgeSentViaWhatsapp && (
                                        <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200 inline-flex items-center gap-1">
                                            <FiSend size={9} className="flex-shrink-0" /> Badge sent
                                        </span>
                                    )}
                                </div>
                            </div>
                        </div>
                        {!selectionMode && (
                            <div className={`flex items-center gap-1.5 px-2 py-1 rounded-full text-xs font-medium flex-shrink-0 ${isActive ? "bg-green-100 text-green-700" : "bg-red-100 text-red-700"}`}>
                                <div className={`w-1.5 h-1.5 rounded-full ${isActive ? "bg-green-500" : "bg-red-500"}`} />
                                {isActive ? "Active" : "Inactive"}
                            </div>
                        )}
                    </div>
                </div>

                <div className="p-4 space-y-2">
                    {person.cin && (
                        <div className="flex items-center gap-2 text-xs">
                            <FiCreditCard className="text-slate-400 flex-shrink-0" size={14} />
                            <span className="text-slate-600">Cin:</span>
                            <span className="text-slate-800 font-medium font-mono">{person.cin}</span>
                        </div>
                    )}
                    {person.phone ? (
                        <div className="flex items-center gap-2 text-xs">
                            <FiPhone className="text-slate-400 flex-shrink-0" size={14} />
                            <span className="text-slate-600">Phone:</span>
                            <span className="text-slate-800 font-medium">{person.phone}</span>
                        </div>
                    ) : (
                        <div className="flex items-center gap-2 text-xs text-red-400">
                            <FiPhone className="flex-shrink-0" size={14} />
                            <span>No phone — cannot send via WhatsApp</span>
                        </div>
                    )}
                    {person.cinPhotos?.some((p) => p) && (
                        <div className="flex gap-2 pt-2">
                            {person.cinPhotos.map((photo, i) => photo && (
                                <img key={i} src={photo} alt={`CIN ${i + 1}`}
                                    onClick={(e) => { e.stopPropagation(); setPhotoPreview(photo); }}
                                    className="w-16 h-12 object-cover rounded border border-slate-200 cursor-pointer hover:border-indigo-400 transition-colors"
                                />
                            ))}
                        </div>
                    )}
                </div>

                {!selectionMode && (
                    <div className="px-4 pb-4 pt-2 border-t border-slate-100 space-y-2">
                        <div className="grid grid-cols-2 gap-2">
                            <button onClick={() => updateStatus(badge._id, "active")} disabled={isActive || actionLoading === badge._id}
                                className={`py-2 px-3 text-xs font-medium rounded-lg transition-colors flex items-center justify-center gap-1.5 ${isActive ? "bg-slate-100 text-slate-400 cursor-not-allowed" : actionLoading === badge._id ? "bg-green-500 text-white" : "bg-green-600 hover:bg-green-700 text-white"}`}>
                                {actionLoading === badge._id ? <div className="animate-spin rounded-full h-3.5 w-3.5 border-2 border-white border-t-transparent" /> : <FiCheckCircle size={14} />}
                                Activate
                            </button>
                            <button onClick={() => updateStatus(badge._id, "inactive")} disabled={!isActive || actionLoading === badge._id}
                                className={`py-2 px-3 text-xs font-medium rounded-lg transition-colors flex items-center justify-center gap-1.5 ${!isActive ? "bg-slate-100 text-slate-400 cursor-not-allowed" : actionLoading === badge._id ? "bg-red-500 text-white" : "bg-red-600 hover:bg-red-700 text-white"}`}>
                                {actionLoading === badge._id ? <div className="animate-spin rounded-full h-3.5 w-3.5 border-2 border-white border-t-transparent" /> : <FiXCircle size={14} />}
                                Deactivate
                            </button>
                        </div>
                        <div className="grid grid-cols-2 gap-2">
                            <button onClick={() => handlePrintBadge(badge, person)}
                                className="py-2 px-3 bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-medium rounded-lg transition-colors flex items-center justify-center gap-1.5">
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" /></svg>
                                Print
                            </button>
                            <button onClick={() => sendWhatsappBadge(badge, person)}
                                disabled={sendingQueue.has(badge._id) || rateLimitInfo !== null || !person.phone || bulkRunning}
                                className={`py-2 px-3 text-xs font-medium rounded-lg transition-colors flex items-center justify-center gap-1.5 ${sendingQueue.has(badge._id) || rateLimitInfo || !person.phone || bulkRunning ? "bg-gray-300 text-gray-500 cursor-not-allowed" : "bg-emerald-600 hover:bg-emerald-700 text-white"}`}
                                title={bulkRunning ? "Bulk send in progress" : !person.phone ? "No phone number" : ""}>
                                {sendingQueue.has(badge._id)
                                    ? <><div className="animate-spin rounded-full h-3.5 w-3.5 border-2 border-white border-t-transparent" /> Sending...</>
                                    : <><FaWhatsapp className="w-4 h-4" /> WhatsApp</>}
                            </button>
                        </div>
                    </div>
                )}
            </div>
        );
    };

    return (
        <>
            <style>{`
                @media print {
                    * { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
                    html, body { margin: 0 !important; padding: 0 !important; width: 105mm !important; height: 148mm !important; overflow: hidden !important; }
                    body * { visibility: hidden !important; }
                    #print-badge-standiste, #print-badge-standiste * { visibility: visible !important; }
                    #print-badge-standiste { position: fixed !important; left: 0 !important; top: 0 !important; width: 105mm !important; height: 148mm !important; margin: 0 !important; padding: 0 !important; overflow: hidden !important; }
                    #print-badge-standiste > div { width: 100% !important; height: 100% !important; }
                    @page { size: 105mm 148mm !important; margin: 0 !important; }
                }
            `}</style>

            {printBadge && (
                <div id="print-badge-standiste">
                    <div style={{ width: "105mm", height: "148mm" }}>
                        <BadgeCard badge={printBadge.badge} standiste={printBadge.person} eventName={standiste?.event?.name || "Event"} badgeConfig={badgeConfig} />
                    </div>
                </div>
            )}

            <div className="min-h-screen">
                <div className="bg-white border-b border-slate-200 sticky top-0 shadow-sm z-10">
                    <div className="px-2 sm:px-4 lg:px-6">
                        <div className="py-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3">
                            <div className="flex items-center gap-4">
                                <button onClick={() => navigate(`/event/${id}/badging`)}
                                    className="flex items-center gap-2 text-slate-600 hover:text-slate-900 hover:bg-slate-100 px-3 py-2 rounded-lg transition-all">
                                    <FiArrowLeft size={20} /><span className="font-medium">Back</span>
                                </button>
                                <div className="hidden sm:block h-8 w-px bg-slate-300" />
                                <div>
                                    <div className="flex items-center gap-2">
                                        <h1 className="text-xl sm:text-2xl font-bold text-slate-900">{standiste.name}'s Team</h1>
                                        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold ${accentColor.pill}`}>
                                            {isContractor ? <FiBriefcase size={10} /> : <FiUser size={10} />}
                                            {roleLabel}
                                        </span>
                                    </div>
                                    <p className="text-sm text-slate-500 mt-0.5">Manage staff members and their access</p>
                                </div>
                            </div>
                            <div className="flex items-center gap-2 flex-wrap">
                                {inactiveCount > 0 && !selectionMode && (
                                    <button onClick={acceptAll} disabled={actionLoading === "accept-all"}
                                        className="flex items-center gap-2 bg-green-600 hover:bg-green-700 disabled:bg-green-400 text-white px-4 py-2.5 rounded-lg font-medium transition-all text-sm">
                                        {actionLoading === "accept-all"
                                            ? <><div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" /> Processing...</>
                                            : <><FiCheckSquare size={16} /> Accept All ({inactiveCount})</>}
                                    </button>
                                )}
                                {["standiste", "contractor"].includes(standiste?.role) && (
                                    <button
                                        onClick={sendAccessEmail}
                                        disabled={actionLoading === "send-email"}
                                        className={`flex items-center gap-2 px-4 py-2.5 rounded-lg font-medium transition-all text-sm text-white ${standiste?.isMailSent
                                            ? "bg-amber-600 hover:bg-amber-700"
                                            : "bg-blue-600 hover:bg-blue-700"
                                            }`}
                                    >
                                        {actionLoading === "send-email" ? (
                                            <>
                                                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" />
                                                Sending...
                                            </>
                                        ) : (
                                            <>
                                                <FiMail size={16} />
                                                {standiste?.isMailSent
                                                    ? "Resend Email"
                                                    : "Send Email"}
                                            </>
                                        )}
                                    </button>
                                )}
                                {!selectionMode ? (
                                    <button onClick={() => setSelectionMode(true)} disabled={bulkRunning}
                                        title={bulkRunning ? "A bulk send is already running" : ""}
                                        className={`flex items-center gap-2 px-4 py-2.5 rounded-lg font-medium transition-all text-sm text-white ${bulkRunning ? "bg-emerald-300 cursor-not-allowed" : "bg-emerald-600 hover:bg-emerald-700"}`}>
                                        <FaWhatsapp size={16} />
                                        {bulkRunning ? "Sending in background…" : "Bulk Send"}
                                    </button>
                                ) : (
                                    <button onClick={exitSelectionMode}
                                        className="flex items-center gap-2 bg-slate-200 hover:bg-slate-300 text-slate-700 px-4 py-2.5 rounded-lg font-medium transition-all text-sm">
                                        <FiX size={16} /> Cancel
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {selectionMode && (
                    <div className="bg-emerald-50 border-b border-emerald-200 px-2 sm:px-4 lg:px-6 py-3">
                        <div className="flex items-center justify-between gap-3 flex-wrap">
                            <div className="flex items-center gap-3">
                                <span className="text-sm font-semibold text-emerald-800">
                                    {selectedIds.size > 0 ? `${selectedIds.size} badge${selectedIds.size > 1 ? "s" : ""} selected` : "Tap badges to select"}
                                </span>
                                <span className="text-xs text-emerald-600 bg-emerald-100 px-2 py-0.5 rounded-full">
                                    Only badges with phone numbers can be selected
                                </span>
                            </div>
                            <div className="flex items-center gap-2">
                                <button onClick={selectAll} className="text-xs text-emerald-700 hover:text-emerald-900 font-medium underline underline-offset-2">Select all</button>
                                <span className="text-emerald-300">|</span>
                                <button onClick={deselectAll} className="text-xs text-emerald-700 hover:text-emerald-900 font-medium underline underline-offset-2">Deselect all</button>
                                <button onClick={handleStartBulkSend} disabled={selectedIds.size === 0}
                                    className={`flex items-center gap-2 px-5 py-2 rounded-lg text-sm font-semibold transition-all ${selectedIds.size === 0 ? "bg-slate-200 text-slate-400 cursor-not-allowed" : "bg-emerald-600 hover:bg-emerald-700 text-white shadow-sm"}`}>
                                    <FiSend size={15} />
                                    Send {selectedIds.size > 0 ? `(${selectedIds.size})` : ""}
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {rateLimitInfo && (
                    <div className="bg-amber-50 border-l-4 border-amber-400 p-4 mb-4 mx-2 sm:mx-4 lg:mx-6 mt-4 rounded-r-lg">
                        <div className="flex items-start">
                            <FiAlertTriangle className="h-5 w-5 text-amber-400 flex-shrink-0" />
                            <div className="ml-3 flex-1">
                                <p className="text-sm text-amber-700"><strong>Rate Limit Active:</strong> {rateLimitInfo.message}</p>
                                <p className="text-xs text-amber-600 mt-1">You can send another message in {rateLimitInfo.retryAfter} seconds.</p>
                            </div>
                            <button onClick={() => setRateLimitInfo(null)} className="flex-shrink-0 ml-3 text-amber-400 hover:text-amber-600"><FiX size={20} /></button>
                        </div>
                    </div>
                )}

                <div className="px-2 sm:px-4 lg:px-6 py-6 space-y-6">

                    <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-5">
                        <div className={`grid gap-6 ${isContractor ? "md:grid-cols-1" : "md:grid-cols-3"}`}>
                            <div>
                                <h3 className="font-semibold text-slate-900 mb-3 flex items-center gap-2 text-sm">
                                    {isContractor ? <FiBriefcase className={accentColor.text} size={16} /> : <FiUser className={accentColor.text} size={16} />}
                                    Contact Info
                                </h3>
                                <div className="space-y-2">
                                    {standiste.email && <div className="flex items-start gap-2 text-xs"><FiMail className="text-slate-400 mt-0.5 flex-shrink-0" size={14} /><span className="text-slate-700 break-all">{standiste.email}</span></div>}
                                    {standiste.phone && <div className="flex items-center gap-2 text-xs"><FiPhone className="text-slate-400 flex-shrink-0" size={14} /><span className="text-slate-700">{standiste.phone}</span></div>}
                                    {standiste.cin && <div className="flex items-center gap-2 text-xs"><FiCreditCard className="text-slate-400 flex-shrink-0" size={14} /><span className="text-slate-700 font-mono">{standiste.cin}</span></div>}
                                </div>
                            </div>
                            {!isContractor && (
                                <div className="md:col-span-2">
                                    <h3 className="font-semibold text-slate-900 mb-3 flex items-center gap-2 text-sm">
                                        <FiMapPin className={accentColor.text} size={16} /> Assigned Stands ({stands.length})
                                    </h3>
                                    <div className="flex flex-wrap gap-2">
                                        {stands.length > 0 ? stands.map((s) => (
                                            <div key={s._id} className="bg-indigo-50 border border-indigo-200 rounded-lg px-3 py-2 text-xs">
                                                <p className="font-semibold text-indigo-900">{s.exhibitorName || "Unnamed"}</p>
                                                <div className="flex flex-wrap gap-x-3 text-indigo-700 mt-1">
                                                    {s.hall && <span><strong>Hall:</strong> {s.hall}</span>}
                                                    {s.tentNumber && <span><strong>Tent:</strong> {s.tentNumber}</span>}
                                                </div>
                                            </div>
                                        )) : <div className="text-slate-500 text-xs py-2">No stands assigned</div>}
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="grid grid-cols-3 gap-3">
                        {[
                            { label: "Total", value: totalCount, dot: "bg-indigo-600", bg: "bg-indigo-50" },
                            { label: "Active", value: activeCount, dot: "bg-green-500", bg: "bg-green-50" },
                            { label: "Inactive", value: inactiveCount, dot: "bg-red-500", bg: "bg-red-50" },
                        ].map((s) => (
                            <div key={s.label} className={`${s.bg} border border-slate-200 rounded-xl p-4 flex items-center gap-3 shadow-sm`}>
                                <div className={`w-3 h-3 rounded-full flex-shrink-0 ${s.dot}`} />
                                <div>
                                    <div className="text-2xl font-bold text-slate-800 leading-none">{s.value}</div>
                                    <div className="text-xs text-slate-500 mt-1">{s.label}</div>
                                </div>
                            </div>
                        ))}
                    </div>

                    <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-2">
                        <div className="flex gap-2">
                            {[
                                { key: "all", label: `All (${totalCount})`, active: accentColor.bg },
                                { key: "active", label: `Active (${activeCount})`, active: "bg-green-600" },
                                { key: "inactive", label: `Inactive (${inactiveCount})`, active: "bg-red-600" },
                            ].map((f) => (
                                <button key={f.key} onClick={() => setFilter(f.key)}
                                    className={`flex-1 py-2.5 px-4 rounded-lg text-sm font-medium transition-all ${filter === f.key ? `${f.active} text-white shadow-sm` : "bg-transparent text-slate-600 hover:bg-slate-100"}`}>
                                    {f.label}
                                </button>
                            ))}
                        </div>
                    </div>

                    {filteredStaff.length === 0 && !standisteBadge ? (
                        <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-12 text-center">
                            <div className="mx-auto w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mb-4">
                                <FiUser className="text-slate-400" size={32} />
                            </div>
                            <h3 className="text-lg font-semibold text-slate-900 mb-2">No staff members found</h3>
                            <p className="text-slate-500 text-sm">{filter === "all" ? "No staff members yet." : `No ${filter} staff members.`}</p>
                        </div>
                    ) : (
                        <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                            {standisteBadge && (
                                <StaffCard badge={standisteBadge}
                                    person={{ name: standiste.name, email: standiste.email, cin: standiste.cin, phone: standiste.phone, cinPhotos: standiste.cinPhotos }}
                                    isManager />
                            )}
                            {filteredStaff.map((badge) => (
                                <StaffCard key={badge._id} badge={badge}
                                    person={{ name: badge.name, cin: badge.cin, phone: badge.phone, email: badge.email, cinPhotos: badge.cinPhotos }} />
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {photoPreview && (
                <div className="fixed inset-0 bg-black/90 flex items-center justify-center z-50 p-4" onClick={() => setPhotoPreview(null)}>
                    <button onClick={() => setPhotoPreview(null)} className="absolute top-4 right-4 text-white bg-black/60 rounded-full p-3"><FiX size={24} /></button>
                    <img src={photoPreview} alt="CIN Preview" className="max-w-full max-h-full object-contain rounded-lg" onClick={(e) => e.stopPropagation()} />
                </div>
            )}

            {confirmOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
                    <div className="bg-white w-full max-w-md rounded-2xl shadow-xl p-6">
                        <div className="flex items-center gap-3 mb-4">
                            <div className="w-10 h-10 rounded-full bg-amber-100 flex items-center justify-center">
                                <FiAlertTriangle className="text-amber-600" size={20} />
                            </div>
                            <h3 className="text-lg font-semibold text-slate-800">Activate All Staff</h3>
                        </div>
                        <p className="text-sm text-slate-600 mb-6">
                            Activate <span className="font-semibold text-slate-900">{pendingCount}</span> inactive staff member{pendingCount > 1 && "s"}?
                        </p>
                        <div className="flex justify-end gap-3">
                            <button onClick={() => setConfirmOpen(false)} className="px-4 py-2 rounded-lg bg-slate-200 hover:bg-slate-300 text-slate-700 font-medium transition">Cancel</button>
                            <button onClick={confirmAcceptAll} disabled={actionLoading === "accept-all"} className="px-4 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white font-medium transition disabled:opacity-50">
                                {actionLoading === "accept-all" ? "Activating..." : "Yes, Activate All"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default StandisteStaff;